import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { postTimeOptions } from 'lib/api/timeOptionsRest';
import {
  buildTimeOptionsPayload,
  getFlowOptionsFromResponse,
} from 'lib/utils/timeOptions';
import { getLatestActivity } from 'lib/selectors/getLatestActivity';
import { getLatestNonIdleInterval } from 'lib/selectors/getLatestNonIdleInterval';

function isAbortError(err) {
  return err?.name === 'AbortError';
}

// Last-resort guard against a hung request (server wedged, network blackhole):
// without it the dialog would show a skeleton forever. Sized well above the
// slowest legitimate request observed (~8s cold stateIntervals query) so it
// never kills a query that would have succeeded.
const TIME_OPTIONS_TIMEOUT_MS = 10000;

/**
 * Fetches time options when enabled; aborts in-flight requests on dependency change or unmount.
 *
 * @param {{ enabled?: boolean, machineRef?: number, flow: string, body: object }} params
 * @returns {{ loading: boolean, error: Error|null, options: Array|null }}
 */
const useTimeOptions = ({ enabled = true, machineRef, flow, body }) => {
  const [options, setOptions] = useState(null);
  const [fetchError, setFetchError] = useState(null);
  const [loading, setLoading] = useState(false);
  const latestActivity = useSelector(getLatestActivity);
  const latestNonIdleInterval = useSelector(getLatestNonIdleInterval);
  const contextRef = useRef({ latestActivity, latestNonIdleInterval });

  contextRef.current = { latestActivity, latestNonIdleInterval };

  useEffect(() => {
    if (!enabled || !machineRef || !flow) {
      setOptions(null);
      setFetchError(null);
      setLoading(false);
      return undefined;
    }

    const controller = new AbortController();
    const input = buildTimeOptionsPayload(body, flow, contextRef.current);

    // Distinguishes a timeout abort (surface as error) from an
    // unmount/dependency-change abort (stay silent).
    let timedOut = false;
    const timeoutId = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, TIME_OPTIONS_TIMEOUT_MS);

    setFetchError(null);
    setOptions(null);
    setLoading(true);

    postTimeOptions(machineRef, input, { signal: controller.signal })
      .then((data) => {
        if (controller.signal.aborted) return;
        const flowOptions = getFlowOptionsFromResponse(data, flow);
        if (!flowOptions?.length) {
          setFetchError(new Error('Time options unavailable'));
          setOptions([]);
          return;
        }
        setOptions(flowOptions);
      })
      .catch((err) => {
        if ((controller.signal.aborted || isAbortError(err)) && !timedOut)
          return;
        setFetchError(
          timedOut ? new Error('Time options request timed out') : err
        );
        setOptions([]);
      })
      .finally(() => {
        clearTimeout(timeoutId);
        // Skip when this run was aborted by unmount/dependency change, so a
        // stale settle can't clobber the loading state of a newer fetch.
        if (controller.signal.aborted && !timedOut) return;
        setLoading(false);
      });

    return () => {
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [enabled, machineRef, flow, body]);

  return {
    loading,
    error: fetchError,
    options,
  };
};

export default useTimeOptions;
